import { ROLE_LABELS, type TenantRole } from '@provalia/shared';
import { Badge, Card, Empty } from '../../shared/ui';
import { fmtDate, fmtDateTime, fmtMoney, fmtPct } from '../../shared/format';
import { toNum, type ActivityRecord, type DeliveryNoteRecord } from '../api';

type Detail = Record<string, unknown>;

const lineNo = (n: unknown): string => (typeof n === 'number' ? `L${n + 1}` : '');
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const arr = (v: unknown): Detail[] => (Array.isArray(v) ? (v as Detail[]) : []);
const signedDays = (n: unknown): string => (typeof n === 'number' && n !== 0 ? ` (${n > 0 ? '+' : ''}${n} d)` : '');

/** Qué pasó, dicho en una frase. */
function describe(a: ActivityRecord, tenantName: string): { title: string; detail: string | null } {
  const d = a.detail ?? {};
  const comment = str(d.comment) ? ` · «${str(d.comment)}»` : '';
  switch (a.action) {
    case 'order.publish':
      return { title: 'Publicado en el portal', detail: d.silent ? 'Primera carga, sin aviso por correo' : 'Se avisó al proveedor por correo' };
    case 'order.accept':
      return { title: 'Pedido aceptado', detail: null };
    case 'order.reject':
      return { title: 'Pedido rechazado', detail: str(d.reason) ? `Motivo: ${str(d.reason)}` : null };
    case 'order.modified': {
      const changes = Array.isArray(d.changes) ? (d.changes as unknown[]).map(String) : [];
      return { title: `Modificado por ${tenantName}`, detail: changes.length ? changes.join(' · ') : 'Vuelve a estar pendiente de aceptar' };
    }
    case 'dates.propose':
      return {
        title: 'Fechas propuestas',
        detail: arr(d.lines)
          .map((l) => `${lineNo(l.lineNum)}: ${fmtDate(str(l.proposed))}${signedDays(l.deviationDays)} ${l.auto ? 'grabada' : 'a validar'}`)
          .join(' · '),
      };
    case 'date.approve':
      return { title: `Fecha aprobada ${lineNo(a.lineNum)}`, detail: `${fmtDate(str(d.proposed))}${comment}` };
    case 'date.reject':
      return { title: `Fecha rechazada ${lineNo(a.lineNum)}`, detail: `${fmtDate(str(d.proposed))}${comment}` };
    case 'delivery_note.submit': {
      const lines = arr(d.lines)
        .map((l) => {
          const dev = toNum(l.deviation as number | null);
          return `${lineNo(l.lineNum)}: ${fmtMoney(toNum(l.price as number))}${dev ? ` (${fmtPct(dev)})` : ''} ${l.status === 'A' ? '👍' : '👎'}`;
        })
        .join(' · ');
      const pdf = d.attachmentError ? ' · El PDF no se pudo adjuntar' : d.attachmentEntry ? ' · PDF adjunto' : '';
      return { title: `Albarán ${str(d.number)} comunicado`, detail: `${lines}${pdf}` };
    }
    case 'price.approve':
      return { title: `Precio aprobado ${lineNo(a.lineNum)}`, detail: `${fmtMoney(toNum(d.price as number))}${comment}` };
    case 'price.reject':
      return { title: `Precio rechazado ${lineNo(a.lineNum)}`, detail: `${fmtMoney(toNum(d.price as number))}${comment}` };
    case 'receipt.confirm':
      return { title: 'Recepción física confirmada', detail: Array.isArray(d.lines) ? (d.lines as number[]).map(lineNo).join(', ') : null };
    case 'drawing.download':
      return { title: `Plano descargado ${lineNo(a.lineNum)}`, detail: `${str(d.itemCode)} v${String(d.version ?? '')}` };
    case 'receipt.undo':
      return { title: 'Recepción física deshecha', detail: Array.isArray(d.lines) ? (d.lines as number[]).map(lineNo).join(', ') : null };
    default:
      return { title: a.action, detail: null };
  }
}

function actorText(a: ActivityRecord): string {
  if (a.actor === 'sistema') return 'Portal (automático)';
  const role = ROLE_LABELS[a.actorRole as TenantRole];
  return role ? `${a.actor} · ${role}` : a.actor;
}

export function ActivityCard({ activity, tenantName }: { activity: ActivityRecord[]; tenantName: string }): JSX.Element {
  return (
    <Card title="Historial">
      {activity.length === 0 ? (
        <Empty icon="🕑">Todavía no hay movimientos en este pedido.</Empty>
      ) : (
        <ol className="timeline">
          {activity.map((a) => {
            const { title, detail } = describe(a, tenantName);
            return (
              <li key={a.id}>
                <div>
                  <div>
                    <strong>{title}</strong>
                  </div>
                  {detail ? <div className="muted pt-wrap">{detail}</div> : null}
                  <div className="subtle">
                    {fmtDateTime(a.at)} · {actorText(a)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

export function DeliveryNotesCard({ notes, currency }: { notes: DeliveryNoteRecord[]; currency: string }): JSX.Element {
  return (
    <Card title="Albaranes comunicados">
      {notes.length === 0 ? (
        <Empty icon="🧾">Todavía no se ha comunicado ningún albarán de este pedido.</Empty>
      ) : (
        <div className="stack">
          {notes.map((n) => (
            <div key={n.id} className="pt-note">
              <div className="row between">
                <strong>Albarán {n.number}</strong>
                <span className="subtle">{fmtDateTime(n.createdAt)}</span>
              </div>
              <div className="subtle">
                {n.createdBy}
                {n.fileName ? ` · PDF: ${n.fileName}` : ' · sin PDF'}
              </div>
              {n.description ? <div className="muted pt-wrap">{n.description}</div> : null}
              <div className="table-wrap">
                <table className="table pt-compact">
                  <thead>
                    <tr>
                      <th>Línea</th>
                      <th className="num">Pedido</th>
                      <th className="num">Albarán</th>
                      <th className="num">Desviación</th>
                      <th>Al comunicarlo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {n.lines.map((l) => (
                      <tr key={l.lineNum}>
                        <td>{lineNo(l.lineNum)}</td>
                        <td className="num">{fmtMoney(toNum(l.orderPrice), currency)}</td>
                        <td className="num">{fmtMoney(toNum(l.price), currency)}</td>
                        <td className="num">{toNum(l.deviationPct) === null ? '—' : fmtPct(toNum(l.deviationPct))}</td>
                        <td>
                          <span aria-hidden="true">{l.status === 'A' ? '👍 ' : '👎 '}</span>
                          <Badge tone={l.status === 'A' ? 'ok' : 'warn'}>{l.status === 'A' ? 'Conforme' : 'Discrepancia: a compras'}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
